/*
 * GLightbox integration for Dawn product media gallery.
 * - Replaces Dawn's native product media modal for images and Shopify-hosted MP4 videos.
 * - Intercepts the click in the CAPTURE phase so Dawn's modal / inline video never fires.
 * - Rebuilds the slide list on every open, so it stays correct after variant changes.
 *
 * Zoom is intentionally delegated to a "Open in new tab" button that opens the full-size
 * image in a new browser tab (reliable native zoom). In-lightbox zoom is disabled, and
 * two-finger pinch is blocked while the lightbox is open, to avoid glitchy behavior.
 *
 * Markup contract (set in snippets/product-thumbnail.liquid):
 *   .product-media-container[data-glb-type]      "image" | "video" | "external_video" | "model"
 *   .product-media-container[data-glb-image]     display-size image URL (images)
 *   .product-media-container[data-glb-full]      full-size image URL for the new tab (images)
 *   .product-media-container[data-glb-video]     highest-quality MP4 URL (Shopify videos)
 *   .product-media-container[data-glb-poster]    poster image URL (videos)
 *   .product-media-container[data-glb-external]  watch URL (YouTube/Vimeo)
 *   .product-media-container[data-glb-alt]       alt text
 */
(function () {
  'use strict';

  var SUPPORTED = ['image', 'video', 'external_video'];
  var lightbox = null; // current instance (rebuilt per open)
  var currentMeta = null; // parallel array describing each slide (type + open-in-tab url)
  var fullBtn = null; // the injected "Open in new tab / Open video" button

  function externalSource(url) {
    if (/youtube\.com|youtu\.be/i.test(url)) return 'youtube';
    if (/vimeo\.com/i.test(url)) return 'vimeo';
    return null;
  }

  // Collect ordered slide configs (for GLightbox) + parallel `nodes` and `meta` arrays.
  function buildSlides(gallery) {
    var slides = [];
    var nodes = [];
    var meta = [];
    var list = gallery.querySelector('.product__media-list');
    if (!list) return { slides: slides, nodes: nodes, meta: meta };

    var items = Array.prototype.filter.call(list.children, function (el) {
      return el.tagName === 'LI';
    });

    items.forEach(function (li) {
      var c = li.querySelector('.product-media-container');
      if (!c) return;

      var type = c.getAttribute('data-glb-type');
      var alt = c.getAttribute('data-glb-alt') || '';
      var slide = null;
      var info = null;

      if (type === 'image' && c.getAttribute('data-glb-image')) {
        slide = { href: c.getAttribute('data-glb-image'), type: 'image', alt: alt };
        info = {
          type: 'image',
          openUrl: c.getAttribute('data-glb-full') || c.getAttribute('data-glb-image'),
          label: 'Open in new tab ↗'
        };
      } else if (type === 'video' && c.getAttribute('data-glb-video')) {
        slide = { href: c.getAttribute('data-glb-video'), type: 'video', source: 'local' };
        info = { type: 'video', openUrl: c.getAttribute('data-glb-video'), label: 'Open video ↗' };
      } else if (type === 'external_video' && c.getAttribute('data-glb-external')) {
        var ext = c.getAttribute('data-glb-external');
        var src = externalSource(ext);
        if (src) {
          slide = { href: ext, type: 'video', source: src };
          info = { type: 'external_video', openUrl: ext, label: 'Open video ↗' };
        }
      }

      if (slide) {
        slides.push(slide);
        nodes.push(c);
        meta.push(info);
      }
    });

    return { slides: slides, nodes: nodes, meta: meta };
  }

  function buildOptions(slides) {
    var cfg = window.RLT_GLIGHTBOX || {};
    var options = {
      elements: slides,
      touchNavigation: true,
      keyboardNavigation: true,
      loop: true,
      zoomable: false, // in-lightbox zoom disabled; use the "Open in new tab" button instead
      draggable: true,
      autoplayVideos: true,
      preload: true,
      openEffect: 'fade',
      closeEffect: 'fade',
      slideEffect: 'slide'
    };

    // Use the self-hosted Plyr build and disable all of Plyr's external requests.
    if (cfg.plyrJs) {
      options.plyr = {
        js: cfg.plyrJs,
        css: cfg.plyrCss,
        config: {
          loadSprite: false,
          iconUrl: '',
          blankVideo: BLANK_VIDEO,
          fullscreen: { iosNative: true }
        }
      };
    }
    return options;
  }

  // Inject the "open in new tab" button into the lightbox UI (once per instance).
  function injectFullButton() {
    var container = document.querySelector('.glightbox-container');
    if (!container) return;
    var existing = container.querySelector('.rlt-glb-fullbtn');
    if (existing) {
      fullBtn = existing;
      return;
    }
    var a = document.createElement('a');
    a.className = 'rlt-glb-fullbtn';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // Keep the click from bubbling to GLightbox's outside-click-close.
    a.addEventListener('click', function (ev) {
      ev.stopPropagation();
    });
    container.appendChild(a);
    fullBtn = a;
  }

  function updateFullButton(i) {
    if (!fullBtn) return;
    var m = currentMeta && currentMeta[i];
    if (m && m.openUrl) {
      fullBtn.href = m.openUrl;
      fullBtn.textContent = m.label;
      fullBtn.style.display = '';
    } else {
      fullBtn.removeAttribute('href');
      fullBtn.style.display = 'none';
    }
  }

  function openFrom(gallery, container) {
    var built = buildSlides(gallery);
    if (!built.slides.length) return false;

    var index = built.nodes.indexOf(container);
    if (index < 0) index = 0;

    if (lightbox) {
      try { lightbox.destroy(); } catch (e) {}
    }
    fullBtn = null;
    currentMeta = built.meta;
    lightbox = GLightbox(buildOptions(built.slides));

    lightbox.on('open', function () {
      injectFullButton();
      var active = typeof lightbox.getActiveSlideIndex === 'function' ? lightbox.getActiveSlideIndex() : index;
      updateFullButton(active);
    });
    lightbox.on('slide_changed', function (data) {
      updateFullButton(data && data.current ? data.current.slideIndex : 0);
      resetZoom();
    });
    lightbox.on('close', function () {
      resetZoom();
    });

    lightbox.openAt(index);
    return true;
  }

  // ---- Open the lightbox from a gallery click (capture phase blocks Dawn's modal) ----
  document.addEventListener(
    'click',
    function (e) {
      if (typeof GLightbox === 'undefined') return;

      var container = e.target.closest && e.target.closest('.product-media-container');
      if (!container) return;

      var gallery = container.closest('media-gallery');
      if (!gallery) return;

      var type = container.getAttribute('data-glb-type');
      if (SUPPORTED.indexOf(type) === -1) return; // 3D model -> Dawn's behavior
      if (type === 'video' && !container.getAttribute('data-glb-video')) return;
      if (type === 'external_video' && !container.getAttribute('data-glb-external')) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      openFrom(gallery, container);
    },
    true
  );

  // ---- Tap outside the media to close, on mobile (GLightbox disables this on mobile) ----
  document.addEventListener('click', function (e) {
    if (!lightbox) return;
    if (!document.body.classList.contains('glightbox-mobile')) return;
    if (!document.body.classList.contains('glightbox-open')) return;

    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('.gslide-media') || t.closest('.gslide-description')) return;
    if (t.closest('.gbtn') || t.closest('.gnext') || t.closest('.gprev') || t.closest('.gclose')) return;
    if (t.closest('.rlt-glb-fullbtn')) return;
    if (!t.closest('.glightbox-container')) return;

    lightbox.close();
  });

  // ---- Block pinch-zoom inside the lightbox (it was glitchy). On mobile, zoom is via
  //      double-tap (below); on desktop, via the "Open in new tab" button. Only two-finger
  //      gestures while the lightbox is open are affected; single-finger swipe and page
  //      zoom elsewhere are untouched. ----
  function lightboxIsOpen() {
    return document.body.classList.contains('glightbox-open');
  }
  document.addEventListener('touchstart', function (e) {
    if (lightboxIsOpen() && e.touches && e.touches.length > 1) {
      e.stopPropagation();
    }
  }, { capture: true, passive: true });
  document.addEventListener('touchmove', function (e) {
    if (lightboxIsOpen() && e.touches && e.touches.length > 1) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, { capture: true, passive: false });
  document.addEventListener('gesturestart', function (e) {
    if (lightboxIsOpen()) e.preventDefault();
  });

  // ---- Mobile double-tap zoom (one fixed level) + drag-to-pan ----
  // Double-tap an image to zoom in, drag to move around, double-tap again to zoom out.
  // Only active on mobile and on image slides. Pinch stays disabled (above).
  var ZOOM_SCALE = 2.5;
  var z = {
    on: false, img: null, tx: 0, ty: 0,
    dragging: false, sx: 0, sy: 0, baseTx: 0, baseTy: 0,
    downX: 0, downY: 0, downT: 0, moved: false,
    lastTapT: 0, lastTapX: 0, lastTapY: 0
  };

  function zoomActive() {
    return document.body.classList.contains('glightbox-mobile') &&
           document.body.classList.contains('glightbox-open');
  }

  function activeImage() {
    var slide = document.querySelector('.glightbox-container .gslide.current');
    return slide ? slide.querySelector('.gslide-media.gslide-image img') : null;
  }

  function applyZoom(useTransition) {
    if (!z.img) return;
    z.img.style.transition = useTransition ? 'transform 0.25s ease' : 'none';
    z.img.style.transformOrigin = 'center center';
    z.img.style.transform =
      'translate3d(' + z.tx + 'px,' + z.ty + 'px,0) scale(' + (z.on ? ZOOM_SCALE : 1) + ')';
  }

  function clampPan() {
    if (!z.img) return;
    var maxX = Math.max(0, (z.img.offsetWidth * ZOOM_SCALE - window.innerWidth) / 2);
    var maxY = Math.max(0, (z.img.offsetHeight * ZOOM_SCALE - window.innerHeight) / 2);
    z.tx = Math.max(-maxX, Math.min(maxX, z.tx));
    z.ty = Math.max(-maxY, Math.min(maxY, z.ty));
  }

  function zoomInAt(x, y) {
    var img = activeImage();
    if (!img) return;
    z.img = img;
    z.on = true;
    z.tx = -(ZOOM_SCALE - 1) * (x - window.innerWidth / 2);
    z.ty = -(ZOOM_SCALE - 1) * (y - window.innerHeight / 2);
    clampPan();
    applyZoom(true);
  }

  function resetZoom() {
    if (z.img) {
      z.img.style.transition = 'transform 0.25s ease';
      z.img.style.transform = '';
      z.img.style.transformOrigin = '';
    }
    z.on = false;
    z.tx = z.ty = 0;
    z.dragging = false;
    z.img = null;
  }

  document.addEventListener('touchstart', function (e) {
    if (!zoomActive() || e.touches.length !== 1) return;
    if (!e.target.closest || !e.target.closest('.gslide-media.gslide-image')) return;
    var t = e.touches[0];
    z.downX = t.clientX; z.downY = t.clientY; z.downT = Date.now(); z.moved = false;
    if (z.on) {
      // Begin a pan; stop GLightbox from treating this as a slide swipe.
      e.stopPropagation();
      z.dragging = true;
      z.sx = t.clientX; z.sy = t.clientY;
      z.baseTx = z.tx; z.baseTy = z.ty;
    }
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!zoomActive() || e.touches.length !== 1) return;
    var t = e.touches[0];
    if (Math.abs(t.clientX - z.downX) > 8 || Math.abs(t.clientY - z.downY) > 8) z.moved = true;
    if (z.on && z.dragging) {
      e.preventDefault();
      e.stopPropagation();
      z.tx = z.baseTx + (t.clientX - z.sx);
      z.ty = z.baseTy + (t.clientY - z.sy);
      clampPan();
      applyZoom(false);
    }
  }, { capture: true, passive: false });

  document.addEventListener('touchend', function (e) {
    if (!zoomActive()) return;
    var startedOnImage = (e.target.closest && e.target.closest('.gslide-media.gslide-image')) || z.dragging;
    var wasDragging = z.dragging;
    z.dragging = false;
    if (!startedOnImage) return;

    // A pan (finger moved) is never a tap.
    if (wasDragging && z.moved) return;
    if (z.moved || Date.now() - z.downT > 350) { z.lastTapT = 0; return; }

    var now = Date.now();
    var isDouble = now - z.lastTapT < 320 &&
      Math.abs(z.downX - z.lastTapX) < 40 && Math.abs(z.downY - z.lastTapY) < 40;

    if (isDouble) {
      e.stopPropagation();
      if (z.on) resetZoom(); else zoomInAt(z.downX, z.downY);
      z.lastTapT = 0;
    } else {
      z.lastTapT = now; z.lastTapX = z.downX; z.lastTapY = z.downY;
    }
  }, { capture: true, passive: false });

  // Tiny black, silent MP4 as a data URI. Plyr uses a "blank" video to cancel network
  // requests when switching sources; this keeps everything self-hosted.
  var BLANK_VIDEO = 'data:video/mp4;base64,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';
})();
